import React, {useState, useEffect, useRef} from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import ScrollAnimation from "react-animate-on-scroll";
import { ANIMATION_DELAY } from "constants";
import * as routes from "../../../../router/constants";

const YOUTUBE_VIDEO_ID = "KovDLD-RB6U";
const UNLOCK_BEFORE_END_SECONDS = 4;

const YT_PLAYER_CONTAINER_ID = "video-page-youtube-player";

const VIDEO_WATCHED_KEY = "video-page-watched";

const hasWatchedVideoInStorage = () => {
  try {
    return localStorage.getItem(VIDEO_WATCHED_KEY) === "true";
  } catch {
    return false;
  }
};

const persistWatchedVideo = () => {
  try {
    localStorage.setItem(VIDEO_WATCHED_KEY, "true");
  } catch {
    // ignore
  }
};

function loadYouTubeIframeAPI() {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(tag, firstScript);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve();
    };
  });
}


export const VideoForceWatch = ({ heading, action = true, videoUrl, color="primary", buttonText="How To Apply", buttonLink }) => {
  const [hasWatchedVideo, setHasWatchedVideo] = useState(hasWatchedVideoInStorage);
  const playerRef = useRef(null);
  const maxWatchedTimeRef = useRef(0);
  const pollIntervalRef = useRef(null);
  const watchedUnlockedRef = useRef(hasWatchedVideoInStorage());

  useEffect(() => {
    let cancelled = false;

    const clearPoll = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const onPlayerStateChange = (event) => {
      if (event.data === window.YT.PlayerState.ENDED) {
        persistWatchedVideo();
        watchedUnlockedRef.current = true;
        setHasWatchedVideo(true);
        clearPoll();
      }
    };

    const enforceProgress = () => {
      const player = playerRef.current;
      if (!player?.getCurrentTime || !player.getPlayerState || !player.getDuration) return;
      const current = player.getCurrentTime();
      const duration = player.getDuration();
      const state = player.getPlayerState();

      if (state === window.YT.PlayerState.PLAYING) {
        maxWatchedTimeRef.current = Math.max(maxWatchedTimeRef.current, current);
      }

      if (current > maxWatchedTimeRef.current + 0.5) {
        player.seekTo(maxWatchedTimeRef.current, true);
      }

      if (
        !watchedUnlockedRef.current &&
        duration > 0 &&
        duration - current <= UNLOCK_BEFORE_END_SECONDS
      ) {
        persistWatchedVideo();
        watchedUnlockedRef.current = true;
        setHasWatchedVideo(true);
        clearPoll();
      }
    };

    const initPlayer = () => {
      if (cancelled || !document.getElementById(YT_PLAYER_CONTAINER_ID)) return;

      playerRef.current = new window.YT.Player(YT_PLAYER_CONTAINER_ID, {
        videoId: YOUTUBE_VIDEO_ID,
        width: "100%",
        height: "100%",
        playerVars: {
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: onPlayerStateChange,
        },
      });

      pollIntervalRef.current = window.setInterval(enforceProgress, 250);
    };

    loadYouTubeIframeAPI().then(() => {
      if (!cancelled) initPlayer();
    });

    return () => {
      cancelled = true;
      clearPoll();
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch {
          // ignore
        }
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <section className="video-section text-center">
      <div className="container">
        <ScrollAnimation
          animateIn="fadeIn"
          delay={ANIMATION_DELAY.default}
          initiallyVisible={false}
          animateOnce={true}
        >
          <h3 className="text-center text-secondary">{heading}</h3>
          <div id={YT_PLAYER_CONTAINER_ID} />

          <p className={`watch-hint ${hasWatchedVideo ? "watch-hint--hidden" : ""}`}>
            The "Apply Now" button will be enabled when you have watched the above video.
          </p>
          {(action || buttonText) && (
            <div className="btn-row">
              {hasWatchedVideo ? (
                <a
                  href={buttonLink}
                  className={`btn btn-${color}`}
                >
                  {buttonText}
                </a>
              ) : (
                <button
                  type="button"
                  className={`btn btn-${color}`}
                  disabled
                >
                  {buttonText}
                </button>
              )}
            </div>
          )}
        </ScrollAnimation>
      </div>
    </section>
  );
};

VideoForceWatch.propTypes = {
  heading: PropTypes.string.isRequired,
  action: PropTypes.bool,
  videoUrl: PropTypes.string.isRequired,
  videoTitle: PropTypes.string,
};

export default VideoForceWatch;
